import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Loader2, RotateCcw, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, setSite } from "../../lib/api";
import { useMe } from "../admin/AdminDashboard";
import { Panel } from "../admin/fields";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Items, Lines, Photo, Photos, Text } from "./webFields";
import { EnquiryList } from "./EnquiryList";

const SECTIONS = [["brand", "Brand"], ["home", "Home"], ["about", "About us"], ["rooms", "Rooms"], ["conference", "Conference"],
  ["restaurant", "Restaurant"], ["gallery", "Gallery"], ["contact", "Contact"], ["events", "Event enquiries"]];

const Section = ({ id, c, set, wid }) => {
  const s = c[id];
  const f = (k) => (v) => set(id, { ...s, [k]: v });
  const t = (k) => `web-${id}-${k}`;
  const box = (title, hint, body) => <Panel title={title} hint={hint}><div className="grid gap-5">{body}</div></Panel>;
  switch (id) {
    case "brand":
      return box("Brand", "Name shown in the header and footer. Upload a logo to replace the text name.", <>
        <div className="grid gap-5 md:grid-cols-3"><Text label="Name" value={s.name} onChange={f("name")} testid={t("name")} />
          <Text label="Second line" value={s.subtitle} onChange={f("subtitle")} testid={t("subtitle")} />
          <Text label="City / country" value={s.city} onChange={f("city")} testid={t("city")} /></div>
        <Text label="Booking page line" value={s.booking_note} onChange={f("booking_note")} testid={t("note")} />
        <Photo wid={wid} label="Logo (optional, light logo on dark background)" value={s.logo_url} onChange={f("logo_url")} testid={t("logo")} />
        <Text label="Website address (where the website files are hosted, e.g. https://equatorhotelarusha.com)" value={s.website_url} onChange={f("website_url")} testid={t("website-url")} />
      </>);
    case "home":
      return box("Home page", "The booking search sits on top of the hero photos.", <>
        <Text label="Hero headline" value={s.hero_title} onChange={f("hero_title")} testid={t("hero-title")} />
        <Text label="Hero line" value={s.hero_subtitle} onChange={f("hero_subtitle")} testid={t("hero-subtitle")} />
        <Photos wid={wid} label="Hero slideshow photos" value={s.hero_images} onChange={f("hero_images")} testid={t("hero-images")} max={8} />
        <Text label="Welcome heading" value={s.intro_title} onChange={f("intro_title")} testid={t("intro-title")} />
        <Text label="Welcome text (blank line = new paragraph)" rows={5} value={s.intro_text} onChange={f("intro_text")} testid={t("intro-text")} />
        <span className="field-label">Highlights</span>
        <Items value={s.highlights} onChange={f("highlights")} blank={{ title: "", text: "" }} addLabel="Add highlight" testid={t("highlights")}
          render={(it, up, i) => <><Text label="Title" value={it.title} onChange={(v) => up({ title: v })} testid={`${t("hl")}-${i}-title`} />
            <Text label="Text" value={it.text} onChange={(v) => up({ text: v })} testid={`${t("hl")}-${i}-text`} /></>} />
      </>);
    case "about":
      return box("About us", null, <>
        <Text label="Title" value={s.title} onChange={f("title")} testid={t("title")} />
        <Text label="Text (blank line = new paragraph)" rows={7} value={s.text} onChange={f("text")} testid={t("text")} />
        <Photo wid={wid} label="Photo" value={s.image} onChange={f("image")} testid={t("image")} />
      </>);
    case "rooms":
      return box("Rooms", "Room descriptions for the website. Live prices and availability come from the booking engine.", <>
        <Text label="Introduction" rows={3} value={s.intro} onChange={f("intro")} testid={t("intro")} />
        <Items value={s.items} onChange={f("items")} blank={{ name: "", occupancy: "", text: "", image: "" }} addLabel="Add room" testid={t("items")}
          render={(it, up, i) => <>
            <Text label="Room name" value={it.name} onChange={(v) => up({ name: v })} testid={`${t("room")}-${i}-name`} />
            <Text label="Sleeps" value={it.occupancy} onChange={(v) => up({ occupancy: v })} testid={`${t("room")}-${i}-occupancy`} />
            <Text label="Description" rows={3} value={it.text} onChange={(v) => up({ text: v })} testid={`${t("room")}-${i}-text`} />
            <Photo wid={wid} label="Photo" value={it.image} onChange={(v) => up({ image: v })} testid={`${t("room")}-${i}-image`} /></>} />
      </>);
    case "conference":
      return box("Conference facilities", null, <>
        <Text label="Title" value={s.title} onChange={f("title")} testid={t("title")} />
        <Text label="Text (blank line = new paragraph)" rows={5} value={s.text} onChange={f("text")} testid={t("text")} />
        <Text label="Package heading" value={s.package_title} onChange={f("package_title")} testid={t("package-title")} />
        <Lines label="Package includes" value={s.package_items} onChange={f("package_items")} testid={t("package-items")} />
        <Photos wid={wid} label="Photos" value={s.images} onChange={f("images")} testid={t("images")} />
      </>);
    case "restaurant":
      return box("Restaurant & bar", null, <>
        <Text label="Title" value={s.title} onChange={f("title")} testid={t("title")} />
        <Text label="Text (blank line = new paragraph)" rows={5} value={s.text} onChange={f("text")} testid={t("text")} />
        <Text label="Bar line" value={s.bar_text} onChange={f("bar_text")} testid={t("bar")} />
        <Photos wid={wid} label="Photos" value={s.images} onChange={f("images")} testid={t("images")} />
      </>);
    case "gallery":
      return box("Photo galleries", "Guests tap a photo to open it full screen and swipe through.",
        <Photos wid={wid} label="Gallery photos" value={s.images} onChange={f("images")} testid={t("images")} max={60} />);
    case "events":
      return <>
        {box("Event enquiry form", "Shown on the Conference and Contact pages. Enquiries are emailed to the events team and saved below.", <>
          <Text label="Heading" value={s.title} onChange={f("title")} testid={t("title")} />
          <Text label="Text" rows={3} value={s.text} onChange={f("text")} testid={t("text")} />
          <Lines label="Events team email addresses (if empty, the hotel's staff alert email is used)" value={s.emails} onChange={f("emails")} testid={t("emails")} />
        </>)}
        <div className="mt-6"><EnquiryList wid={wid} /></div>
      </>;
    default:
      return box("Contact", "The map uses the map search text, e.g. the hotel name and street.", <>
        <Text label="Address" value={s.address} onChange={f("address")} testid={t("address")} />
        <div className="grid gap-5 md:grid-cols-2"><Lines label="Phone numbers" value={s.phones} onChange={f("phones")} testid={t("phones")} />
          <Lines label="Email addresses" value={s.emails} onChange={f("emails")} testid={t("emails")} /></div>
        <Text label="Map search text" value={s.map_query} onChange={f("map_query")} testid={t("map")} />
      </>);
  }
};

const clean = (c) => JSON.parse(JSON.stringify(c, (k, v) => (Array.isArray(v) ? v.filter((x) => x !== "") : v)));

export default function WebsiteEditor() {
  const { wid } = useParams();
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["website", wid], queryFn: () => api.get(`/web/${wid}`).then((r) => r.data), enabled: !!me.data });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.data) {
      setSite(q.data.site_slug);
      setDraft(q.data.content);
    }
  }, [q.data]);
  if (me.isError) return <Navigate to="/admin/login" replace />;
  if (q.isError) return <p className="p-10 text-red-600" data-testid="web-load-error">{apiError(q.error)}</p>;
  if (!draft) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  const dirty = JSON.stringify(draft) !== JSON.stringify(q.data.content);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      qc.setQueryData(["website", wid], (await fn()).data);
      toast.success(msg);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  const save = () => run(() => api.put(`/web/${wid}`, { content: clean(draft) }), "Website saved. Refresh the website to see it.");
  const reset = () => window.confirm("Replace all website text and photos with the starter content?") && run(() => api.post(`/web/${wid}/reset`), "Starter content restored");
  const back = me.data.platform_role === "superadmin" ? "/superadmin" : "/admin";
  return (
    <Tabs defaultValue="brand" className="min-h-screen bg-linen font-sans text-ink" data-testid="website-editor">
      <header className="sticky top-0 z-40 border-b border-ink/10 bg-white/90 backdrop-blur-xl">
        <div className="container-x flex flex-wrap items-center justify-between gap-3 py-3">
          <div><p className="font-serif text-lg leading-tight" data-testid="web-editor-title">{draft.brand.name} {draft.brand.subtitle}</p><p className="eyebrow">Website content</p></div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to={back} className="btn-ghost" data-testid="web-back"><ArrowLeft className="h-4 w-4" />Back</Link>
            {draft.brand.website_url && <a href={draft.brand.website_url} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="web-view"><ExternalLink className="h-4 w-4" />View website</a>}
            <button className="btn-ghost" disabled={busy} onClick={reset} data-testid="web-reset"><RotateCcw className="h-4 w-4" />Starter content</button>
            <button className="btn-primary !py-2 disabled:opacity-40" disabled={!dirty || busy} onClick={save} data-testid="web-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save
            </button>
          </div>
        </div>
        <div className="container-x overflow-x-auto">
          <TabsList className="h-auto bg-transparent p-0">
            {SECTIONS.map(([id, label]) => <TabsTrigger key={id} value={id} className="rounded-none border-b-2 border-transparent px-4 py-3 data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid={`web-tab-${id}`}>{label}</TabsTrigger>)}
          </TabsList>
        </div>
      </header>
      <main className="container-x py-8">
        {SECTIONS.map(([id]) => <TabsContent key={id} value={id}><Section id={id} c={draft} set={set} wid={draft.brand.website_url} /></TabsContent>)}
      </main>
    </Tabs>
  );
}
